import React, { memo, useCallback, useContext, useMemo } from 'react';
import { observable } from 'mobx';
import { useLocalStore } from 'mobx-react-lite';
import isBoolean from 'lodash/isBoolean';
import { FormProvider } from 'choerodon-ui/pro/lib/form/FormContext';
import ConfigContext from './ConfigContext';
import { isCustomizable } from '../configure/utils';
const ConfigProvider = function ConfigProvider(props) {
    const { getConfig: getParentConfig } = useContext(ConfigContext);
    const { children, ...localConfig } = props;
    const configStore = useLocalStore((config) => ({
        config,
    }), observable.map(localConfig));
    const getLocalConfig = useCallback((key) => {
        const localValue = configStore.config.get(key);
        if (configStore.config.has(key)) {
            return localValue;
        }
        return getParentConfig(key);
    }, [configStore, getParentConfig]);
    const getLocalPrefixCls = useCallback((suffixCls, customizePrefixCls) => {
        if (customizePrefixCls) {
            return customizePrefixCls;
        }
        return `${getLocalConfig('prefixCls')}-${suffixCls}`;
    }, [getLocalConfig]);
    const getLocalProPrefixCls = useCallback((suffixCls, customizePrefixCls) => {
        if (customizePrefixCls) {
            return customizePrefixCls;
        }
        return `${getLocalConfig('proPrefixCls')}-${suffixCls}`;
    }, [getLocalConfig]);
    const getLocalCustomizable = useCallback((component) => {
        const customizable = getLocalConfig('customizable');
        if (isBoolean(customizable)) {
            return customizable;
        }
        if (isCustomizable(customizable)) {
            return customizable[component];
        }
    }, [getLocalConfig]);
    const getLocalUsefulTooltip = useCallback((target) => {
        switch (target) {
            case 'table-cell':
                return getLocalConfig('tableColumnTooltip');
            case 'button':
                return getLocalConfig('buttonTooltip');
            case 'select-option':
                return getLocalConfig('selectOptionTooltip');
            case 'label':
                return getLocalConfig('labelTooltip');
            default:
        }
    }, [getLocalConfig]);
    const getLocalTooltip = useCallback((target) => {
        const tooltip = getLocalConfig('tooltip');
        if (typeof tooltip === 'function') {
            return tooltip(target);
        }
        return getLocalUsefulTooltip(target) || tooltip;
    }, [getLocalConfig, getLocalUsefulTooltip]);
    const getLocalTooltipTheme = useCallback((target) => {
        const tooltipTheme = getLocalConfig('tooltipTheme');
        if (typeof tooltipTheme === 'function') {
            return tooltipTheme(target);
        }
        if (target === 'validation') {
            const validationTooltipTheme = getLocalConfig('validationTooltipTheme');
            if (validationTooltipTheme) {
                return validationTooltipTheme;
            }
        }
        return tooltipTheme;
    }, [getLocalConfig]);
    const getLocalTooltipPlacement = useCallback((target) => {
        const tooltipPlacement = getLocalConfig('tooltipPlacement');
        if (typeof tooltipPlacement === 'function') {
            return tooltipPlacement(target);
        }
        return tooltipPlacement;
    }, [getLocalConfig]);
    const value = useMemo(() => ({
        getConfig: getLocalConfig,
        getPrefixCls: getLocalPrefixCls,
        getProPrefixCls: getLocalProPrefixCls,
        getCustomizable: getLocalCustomizable,
        getTooltip: getLocalTooltip,
        getTooltipTheme: getLocalTooltipTheme,
        getTooltipPlacement: getLocalTooltipPlacement,
    }), [getLocalConfig, getLocalPrefixCls, getLocalProPrefixCls, getLocalCustomizable, getLocalTooltip, getLocalTooltipTheme, getLocalTooltipPlacement]);
    return (React.createElement(ConfigContext.Provider, { value: value },
        React.createElement(FormProvider, null, children)));
};
ConfigProvider.displayName = 'ConfigProvider';
export default memo(ConfigProvider);
